/**
 * The wolves come down (M5-02).
 *
 * The valley's first move against the player, and the ticket's whole argument
 * is that it must be **simulated pressure rather than scripted theatre**. So
 * there is no raid script anywhere in this file. What is here is a set of
 * conditions and a dispatch:
 *
 * - a den that has not eaten (`denIsHungry`) has a reason to come,
 * - a night with cloud on it gives it cover,
 * - fires on the approach take that cover away,
 * - and if all of that lines up, two to four wolves are sent *walking* — with
 *   an errand and nothing else. From the moment they set off, every decision
 *   they make is the ordinary `beasts.ts` state machine: they perceive the
 *   livestock because a predator notices `docile` animals (M5-01), they close
 *   because that is what a predator does, and they break off when their nerve
 *   goes, exactly as they would on the moor.
 *
 * That is what makes the improvised defences work. A watch fire deters a raid
 * because this file asks whether there is fire near the pen, not because
 * anything anywhere implements a tower. Two players standing in the fold repel
 * one because a wolf reads two enemies within a stride as bad odds — the same
 * `morale` call it has always made.
 *
 * ## What a raid leaves behind
 *
 * All of it is real state, and none of it is bookkeeping this module does. The
 * dead animal is a carcass with a species on it; the losses show up in the pen
 * census because the census is *counting the actors*; the rumour the herders
 * repeat in the morning is `chronicle.ts` reading the same `died` event
 * everything else read; and the trail back to the den is `tracking.ts`'s
 * ordinary sign, laid by the ordinary move handler, because a raiding wolf
 * walks like every other wolf.
 *
 * The one thing this file adds to the fight itself is the **drag**: a wolf that
 * has killed something takes it, which puts it on M4-06's burden and makes it
 * slow. A laden wolf is a catchable wolf, and a killed one drops the carcass
 * where it falls. That is the counterattack the raid is really for.
 */
import { chebyshev } from './actor.js';
import { fireAt } from './camp.js';
import { denIsHungry, feedDen } from './ecology.js';
import { isDown } from './vitals.js';
import { gameTime, TICKS_PER_GAME_HOUR } from './time.js';
import { isRaining, skyPhase } from './weather.js';
/* -------------------------------------------------------------------------- *
 * The numbers
 * -------------------------------------------------------------------------- */
/** How many wolves come. Two is a raid; five would be the end of the village. */
export const RAID_PARTY_MIN = 2;
export const RAID_PARTY_MAX = 4;
/**
 * Chance per night hour that a hungry pack comes down, before anything modifies
 * it.
 *
 * Quoted per hour and small on purpose. There are about eight night hours in a
 * game day, so a den that is hungry all night and undeterred raids on roughly
 * one night in three — which over a week of play is two or three raids from any
 * one den, before the governor's ceiling ever comes into it.
 */
export const RAID_BASE_CHANCE = 0.05;
/**
 * What cloud is worth.
 *
 * The ticket asks for "rainy, moonless nights". There is no moon in the sim, and
 * inventing one for this would be a system built to serve a probability — so
 * cloud stands in for it, which is the same fact from the other side: the nights
 * you cannot see on are the overcast ones and the wet ones. Fog is the worst of
 * all, and rain is close behind because it takes the scent of a fire with it.
 */
export const RAID_WEATHER_BIAS = {
    clear: 0.6,
    wind: 0.8,
    overcast: 1.4,
    fog: 1.8,
    rain: 1.6,
    'heavy-rain': 1.8,
};
/**
 * How far from the fold a fire has to be before it stops counting as a watch
 * fire.
 *
 * Eight tiles: near enough that somebody deliberately camped by the pen is
 * covering it, far enough that the hearth indoors is not doing the work for
 * them. Standing a fire here is the improvised defence the ticket wants to
 * reward, and it has to be a decision with a place in it.
 */
export const WATCH_FIRE_RANGE = 8;
/**
 * What one watch fire is worth, multiplied per fire.
 *
 * 0.35 each, so one fire cuts a raid's odds by about two thirds and two make it
 * most of a quiet night — without ever reaching zero, because a defence that is
 * *perfect* is a defence players stop thinking about after the first week.
 */
export const WATCH_FIRE_FACTOR = 0.35;
/**
 * How thin the herd may get before the valley stops taking from it.
 *
 * The MVP must not eat its own stakes. Below this many animals the raids stop
 * entirely: what makes M5-04's boar mean anything is a village that still has
 * something left to lose, and a simulation that ate the herd in week one would
 * have spent the campaign's stakes on its own opening move.
 */
export const PEN_FLOOR = 2;
/** Ceiling and spacing on raids, whatever the dice say. See `pressure.ts`. */
export const RAIDS_PER_WEEK = 4;
export const RAID_COOLDOWN = TICKS_PER_GAME_HOUR * 10;
/**
 * How long a raiding party keeps walking before it gives up and goes home.
 *
 * Six game hours, which is most of a night. A deadline rather than a distance
 * so that a pack which cannot find a way round a crag stops instead of walking
 * into it until dawn.
 */
export const RAID_ERRAND_TICKS = TICKS_PER_GAME_HOUR * 6;
/** Is this actor one of the village's animals? The `docile` test, as everywhere. */
function isLivestock(world, catalogue, id) {
    const mind = world.minds[id];
    if (!mind)
        return false;
    return catalogue.instinct?.(mind.instinctId)?.disposition === 'docile';
}
/**
 * Every animal of the village's that is still standing, by id.
 *
 * The pen census, and it is *counted* rather than stored — which is the whole
 * reason losses propagate without anybody writing propagation. There is no
 * second number anywhere that could disagree with how many animals are actually
 * in the world.
 */
export function penCensus(world, catalogue) {
    const found = [];
    for (const id of Object.keys(world.actors).sort()) {
        if (!isLivestock(world, catalogue, id))
            continue;
        if (world.combat[id]?.vitals.dead)
            continue;
        found.push(id);
    }
    return found;
}
/**
 * Where the fold is, as far as a wolf is concerned: the middle of whatever the
 * village still has.
 *
 * Derived from the animals rather than from a coordinate in world state, and
 * deliberately: the sim does not know what a "village site" is and should not
 * start to. The pen is where the livestock are, which is both true and means
 * that the day content puts a second fold in a second valley there is nothing
 * here to change.
 */
export function penCentre(world, catalogue) {
    const herd = penCensus(world, catalogue);
    if (herd.length === 0)
        return null;
    let x = 0;
    let y = 0;
    for (const id of herd) {
        const actor = world.actors[id];
        if (!actor)
            continue;
        x += actor.x;
        y += actor.y;
    }
    return { x: Math.round(x / herd.length), y: Math.round(y / herd.length) };
}
/** How many watch fires are burning near a place right now. */
export function watchFires(world, at, tick) {
    let count = 0;
    for (const light of Object.values(world.lights)) {
        if (light.kind !== 'fire')
            continue;
        if (tick >= light.outAt)
            continue;
        if (chebyshev(light.x, light.y, at.x, at.y) <= WATCH_FIRE_RANGE)
            count += 1;
    }
    return count;
}
/** The wolves of this den that are alive and in a state to walk anywhere. */
function ableWolves(world, catalogue, den, tick) {
    return den.living.filter((id) => {
        const actor = world.actors[id];
        const combatant = world.combat[id];
        if (!actor || !combatant || combatant.vitals.dead)
            return false;
        const plan = catalogue.plan(combatant.loadout.planId);
        if (plan && isDown(plan, combatant.vitals, tick))
            return false;
        // Already out on one. A pack does not get sent twice.
        return !world.minds[id]?.errand;
    });
}
/** Dens that could raid tonight, hungriest — that is, longest unfed — first. */
export function hungryDens(world, catalogue, tick) {
    const found = [];
    for (const id of Object.keys(world.dens).sort()) {
        const den = world.dens[id];
        if (!den)
            continue;
        if (!denIsHungry(den, tick))
            continue;
        if (ableWolves(world, catalogue, den, tick).length < RAID_PARTY_MIN)
            continue;
        // Only things that hunt come down. A warren does not raid anybody.
        if (catalogue.instinct?.(den.speciesId)?.disposition !== 'predator')
            continue;
        found.push(den);
    }
    return found.sort((a, b) => (a.fedAt ?? 0) - (b.fedAt ?? 0) || a.id.localeCompare(b.id));
}
/* -------------------------------------------------------------------------- *
 * The rule
 * -------------------------------------------------------------------------- */
/**
 * How likely a raid is this hour, all told — and it is exported because it is
 * the ticket's second acceptance criterion stated as a function.
 *
 * "Watch fires on the approach measurably reduce raid probability" is a claim a
 * test should be able to make by calling something, rather than by running ten
 * thousand nights and squinting at the difference.
 */
export function raidChance(ctx) {
    const world = ctx.world;
    const centre = penCentre(world, ctx.catalogue);
    if (!centre)
        return 0;
    const bias = RAID_WEATHER_BIAS[world.weather.kind] ?? 1;
    const fires = watchFires(world, centre, ctx.tick);
    const deterrence = WATCH_FIRE_FACTOR ** fires;
    return RAID_BASE_CHANCE * bias * deterrence;
}
/**
 * The raid, as a `PressureRule`.
 *
 * Everything hard is in `when` and everything soft is in `chance`, and the split
 * is the ticket's: a sated den does not raid *at all*, and a clear night is
 * merely a worse night for it. The two must not be confused, because a hard
 * condition expressed as a small probability is a bug that only shows up on the
 * thousandth roll.
 */
export const raidRule = {
    id: 'wolf-raid',
    perWeek: RAIDS_PER_WEEK,
    cooldown: RAID_COOLDOWN,
    when(ctx) {
        const world = ctx.world;
        // Wolves come at night. Not because they cannot see by day — because a raid
        // the whole village watches walk in is not a raid, it is a battle, and the
        // one the players are meant to have is the one they wake up to.
        if (skyPhase(gameTime(ctx.tick)) !== 'night')
            return false;
        // Never while the herd is already down to its last. See `PEN_FLOOR`.
        if (penCensus(world, ctx.catalogue).length <= PEN_FLOOR)
            return false;
        return hungryDens(world, ctx.catalogue, ctx.tick).length > 0;
    },
    chance: raidChance,
    fire(ctx, rng) {
        const world = ctx.world;
        const den = hungryDens(world, ctx.catalogue, ctx.tick)[0];
        const centre = penCentre(world, ctx.catalogue);
        if (!den || !centre)
            return;
        const able = ableWolves(world, ctx.catalogue, den, ctx.tick);
        // `int` takes an exclusive upper bound, so the party can actually be four.
        const wanted = rng.int(RAID_PARTY_MIN, Math.min(RAID_PARTY_MAX, able.length) + 1);
        const party = able.slice(0, wanted);
        if (party.length < RAID_PARTY_MIN)
            return;
        for (const id of party) {
            const mind = world.minds[id];
            if (!mind)
                continue;
            mind.errand = {
                x: centre.x,
                y: centre.y,
                until: ctx.tick + RAID_ERRAND_TICKS,
                kind: 'raid',
            };
        }
        ctx.emit({
            type: 'status',
            actors: party,
            refs: ['raid', 'set-out', den.id, String(party.length)],
            text: 'Somewhere up the valley, a pack gets to its feet.',
            // Archived, not narrated. Nobody in the village is awake to hear this and
            // a player who was *told* a raid had started would never have to keep a
            // watch. What they get is the herders shouting when it arrives.
            audience: 'archive',
            at: { x: den.x, y: den.y },
        });
    },
};
/* -------------------------------------------------------------------------- *
 * Being on one
 * -------------------------------------------------------------------------- */
/**
 * Should this animal turn round and go home?
 *
 * Called from the errand walk each time it takes a step. Three ways an errand
 * ends other than by arriving: the night runs out, the pen is empty, or there is
 * fire in front of it — and it is the third that makes a watch fire worth
 * lighting *after* a raid has already set off. The deterrence in `raidChance`
 * is the odds; this is the wolf.
 */
export function abandonsErrand(ctx, actor, mind) {
    const errand = mind.errand;
    if (!errand)
        return true;
    if (ctx.tick >= errand.until)
        return true;
    const world = ctx.world;
    // A fire it is nearly standing in. Deliberately checked at the wolf rather
    // than at the pen, so a line of brands carried by a guard rotation turns a
    // pack back on the approach — which is exactly the improvised defence the
    // ticket wants working through the simulation.
    return fireAt(world, actor.x, actor.y, ctx.tick) !== null;
}
/**
 * Take the kill (M5-02's drag).
 *
 * A wolf that has killed something of the village's picks it up and starts for
 * home. It goes on M4-06's burden, which means it moves at 0.4 — a laden wolf
 * is a catchable wolf, and killing it drops the carcass exactly where it falls,
 * because `carry.ts` already puts a burden down when its bearer stops being able
 * to hold one.
 *
 * Returns the carcass it took, for the log.
 */
export function takeCarcass(world, actor, tick) {
    const combatant = world.combat[actor.id];
    const mind = world.minds[actor.id];
    if (!combatant || combatant.burden || !mind)
        return null;
    for (const id of Object.keys(world.carcasses).sort()) {
        const carcass = world.carcasses[id];
        if (!carcass)
            continue;
        if (chebyshev(actor.x, actor.y, carcass.x, carcass.y) > 1)
            continue;
        // Not one another wolf already has hold of. Two animals dragging the same
        // ewe in opposite directions is the litter rule (`carry.ts`) firing on a
        // pair who did not agree to co-operate, and it looks exactly as silly as it
        // sounds — the second wolf should go and kill its own.
        if (heldAlready(world, id))
            continue;
        combatant.burden = { targetId: id, mode: 'drag', since: tick };
        // Home, and by the shortest way. It has what it came for.
        mind.errand = { x: mind.homeX, y: mind.homeY, until: tick + RAID_ERRAND_TICKS, kind: 'raid' };
        return id;
    }
    return null;
}
/** Has anything already got hold of this? */
function heldAlready(world, carcassId) {
    for (const combatant of Object.values(world.combat)) {
        if (combatant.burden?.targetId === carcassId)
            return true;
    }
    return false;
}
/* -------------------------------------------------------------------------- *
 * The alarm
 * -------------------------------------------------------------------------- */
/**
 * How far a herder's shout carries. Further than speech and further than a
 * scream: it is the whole of the village's early warning, and a warning nobody
 * in the hall can hear is not one.
 */
export const ALARM_RANGE = 24;
/** How long before a villager will raise the alarm again. */
export const ALARM_COOLDOWN = TICKS_PER_GAME_HOUR;
/**
 * The herders yell (M5-02).
 *
 * A system rather than something the raid rule does when it fires, and the
 * difference is the whole honesty of it: the alarm is raised when somebody who
 * lives here **can actually see a wolf**, which means it is raised late, it is
 * raised by whoever happens to be awake, and it is not raised at all if the
 * herders are dead. A raid announced by the scheduler would be the game telling
 * a player something no character knows.
 *
 * Deri's three-in-the-morning stop at the fold (M5-01) is what usually makes
 * this fire. That was written as a schedule entry a milestone before there was
 * anything to see, and it is the reason the village has a watchman at all.
 */
export const alarmSystem = {
    name: 'alarm',
    phase: 'sim',
    run(ctx) {
        const world = ctx.world;
        // Once a game minute is plenty for a shout, and it keeps a village of five
        // from asking "can I see a wolf" fifty times a second.
        if (ctx.tick % 25 !== 0)
            return;
        for (const id of Object.keys(world.villagers ?? {}).sort()) {
            const actor = world.actors[id];
            const combatant = world.combat[id];
            if (!actor || !combatant || combatant.vitals.dead)
                continue;
            if (actor.sayReadyAt > ctx.tick)
                continue;
            const raider = raiderNear(world, ctx.catalogue, actor, ctx.tick);
            if (!raider)
                continue;
            // Rate-limited through the same field speech is, so a herder standing
            // next to a pack for a minute shouts once rather than six hundred times.
            actor.sayReadyAt = ctx.tick + ALARM_COOLDOWN;
            ctx.emit({
                type: 'said',
                actors: [actor.id, raider.id],
                refs: ['villager', 'alarm'],
                text: `${actor.name}: "Wolves! Wolves in the fold!"`,
                audience: 'earshot',
                range: ALARM_RANGE,
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
/** A raiding animal this villager can see, or `undefined`. */
function raiderNear(world, catalogue, villager, tick) {
    for (const id of Object.keys(world.minds).sort()) {
        const mind = world.minds[id];
        const actor = world.actors[id];
        const combatant = world.combat[id];
        if (!mind || !actor || !combatant || combatant.vitals.dead)
            continue;
        if (catalogue.instinct?.(mind.instinctId)?.disposition !== 'predator')
            continue;
        // On an errand, or already at the animals. Both are "there is a wolf in the
        // village", and the second is the one that matters most.
        const raiding = mind.errand != null || combatant.burden != null;
        const hunting = mind.targetId !== null && isLivestock(world, catalogue, mind.targetId);
        if (!raiding && !hunting)
            continue;
        // Eyesight, roughly: what a person standing in the dark would actually
        // notice. Not `perceive`, because a villager is not an animal and does not
        // have an `Instinct` to read senses off.
        if (chebyshev(villager.x, villager.y, actor.x, actor.y) > alarmSight(world, tick))
            continue;
        return actor;
    }
    return undefined;
}
/**
 * How far a villager notices a wolf in the dark.
 *
 * Short, and shorter in the rain. This is the number that makes a raid
 * frightening: by the time anybody has seen it, it is in the fold. A watchman
 * with a fire beside him sees further, which is one more reason to light one.
 */
function alarmSight(world, tick) {
    const night = skyPhase(gameTime(tick)) === 'night';
    if (!night)
        return 12;
    return isRaining(world.weather.kind) ? 3 : 5;
}
/* -------------------------------------------------------------------------- *
 * Feeding the dens
 * -------------------------------------------------------------------------- */
/**
 * Offer one death to the dens (M5-02).
 *
 * Called from `advance`'s emitter beside `chronicle.ts`'s `remember`, and for
 * exactly the same reason: it is the one place every event in the game passes
 * through, and a rule that had to remember to feed its own den is a rule that
 * will eventually forget.
 *
 * A den is fed by *anything* its animals kill. That is the sentence the whole
 * raid loop hangs off: a valley with rabbits in it is a valley whose wolves stay
 * up on the moor, and a valley players have hunted bare is a valley whose wolves
 * come down. The pressure is a consequence of play rather than a schedule.
 */
export function feedDens(world, event) {
    if (event.type !== 'died')
        return;
    // The dead first, then whoever did it — the `died` convention.
    const killerId = event.actors[1];
    if (killerId === undefined)
        return;
    const killer = world.actors[killerId];
    const mind = world.minds[killerId];
    if (!killer || killer.kind !== 'creature' || !mind)
        return;
    for (const den of Object.values(world.dens)) {
        if (!den.living.includes(killerId))
            continue;
        feedDen(den, event.tick);
        return;
    }
}
/* -------------------------------------------------------------------------- *
 * Wiring
 * -------------------------------------------------------------------------- */
/** Everything M5-02 adds to the tick. The scheduler itself is `advance`'s. */
export const RAID_SYSTEMS = [alarmSystem];
export const PRESSURE_RULES = [raidRule];
//# sourceMappingURL=raids.js.map